import { createSelector } from 'reselect'

import { getBreadcrumbsFromCategory } from '@/components/shared/breadcrumbs'
import { CATEGORY_IDS, MAX_DRIVING_DISTANCE, PERFORMER_IDS, PERFORMER_PAGE } from '@/constants'
import type { ProductionListParams } from '@/context/productions-list/types'
import type { Performer } from '@/types/app-types'

import type { PerformerStateType } from './types'

export const singleRecordSelector = createSelector(
    (state: PerformerStateType) => state.data?.items || [],
    (performers: Performer[]): Performer => performers[0] || {},
)

export const pageTitleSelector = createSelector(singleRecordSelector, (performer: Performer) => {
    const { title, name, subtitle = '' } = performer
    const isParking = name?.toLowerCase().endsWith('parking') ?? true
    return { title: title || `${name}${isParking ? '' : ' Tickets'}`, subtitle }
})

/**
 * Select the performer page's URL. Prioritizes webPath over organicURL
 */
export const performerPageURLSelector = createSelector(singleRecordSelector, (performer: Performer) => {
    const { webPath, organicUrl } = performer
    return webPath || organicUrl
})

export const breadcrumbSelector = createSelector(singleRecordSelector, (performer: Performer) => {
    // TODO: Set 'isExternalLink' flag to false once category/subcategory pages are implemented in Next.js
    return getBreadcrumbsFromCategory(performer.category, { isExternalLink: true })
})

export const getCategory: any = createSelector(
    singleRecordSelector,
    (
        performer: Performer,
    ): {
        [k: string]: number
    } | null => {
        const category = performer.category

        const subCategories = performer.category?.subCategories
        if (!category && !subCategories) {
            return null
        }
        return {
            [subCategories?.length ? 'subCategoryId' : 'categoryId']: subCategories?.length
                ? subCategories[0].id
                : category.id,
        }
    },
)

export const getSubCategoryAndCategoryId = createSelector(
    singleRecordSelector,
    (
        performer: Performer,
    ): {
        categoryId: number
        subCategoryId?: number
    } | null => {
        const category = performer.category

        const subCategories = performer.category?.subCategories
        if (!category && !subCategories) {
            return null
        }
        return {
            subCategoryId: subCategories?.length ? subCategories[0].id : undefined,
            categoryId: category.id,
        }
    },
)

export const productionListTitleSelector = createSelector(singleRecordSelector, (performer: Performer) => {
    const titlesByCategory: { [P in CATEGORY_IDS]: (name: string, performerId: number) => string } = {
        [CATEGORY_IDS.CONCERTS]: (name: string) => `${name} Tour Dates`,
        [CATEGORY_IDS.THEATER]: (name: string) => `${name} Performances`,
        [CATEGORY_IDS.SPORTS]: (name: string, performerId: number) =>
            `${name} ${performerId === PERFORMER_IDS.SUPERBOWL ? 'Tickets' : 'Schedule'}`,
    }
    const { category, name, id } = performer
    if (!category || !category.id) return 'All Tour Dates'
    return titlesByCategory[category.id as CATEGORY_IDS](name, id)
})

export const relatedEventsParamsSelector = createSelector(getCategory, (categoryParams: ProductionListParams) => {
    return {
        ...categoryParams,
        pageSize: PERFORMER_PAGE.MAX_RELATED_EVENTS,
        sortBy: 'RANK',
        radius: MAX_DRIVING_DISTANCE,
        includeIpAddress: true,
    }
})
